import { ref, computed } from 'vue'
import { useApi } from '@/composables/useApi'
import type { PersonnelOption } from '@/types/Personnel'

export function usePersonnelList() {
  const { apiDjangoAuth } = useApi()
  
  const personnels = ref<PersonnelOption[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  const personnelsForSelect = computed(() => {
    return personnels.value.map(p => ({
      id: p.id,
      title: `${p.personne.nom} ${p.personne.prenom} (${p.type || p.matricule})`,
      subtitle: p.personne.email,
      value: p.id
    }))
  })

  // Fonction pour extraire le type depuis les groupes
  function extractTypeFromGroups(p: any): string {
    // Si le type est déjà présent, l'utiliser
    if (p.type && p.type.trim() !== '') {
      return p.type
    }
    
    // Extraire depuis les groupes de l'utilisateur
    const groups = p.personne?.user?.groups || p.user?.groups || []
    const groupNames = groups.map((g: any) => g.name || g).filter(Boolean)
    
    // Déterminer le type selon les groupes
    if (groupNames.some((name: string) => name === 'Enseignant' || name === 'Enseignement')) {
      return 'Enseignement'
    }
    if (groupNames.some((name: string) => 
      name === 'Administration' || 
      name === 'Admin' || 
      name === 'Administrateur' ||
      name === 'Secrétaire' ||
      name === 'Comptable' ||
      name === 'RH'
    )) {
      return 'Administration'
    }
    
    return ''
  }

  async function fetchPersonnels() {
    loading.value = true
    error.value = null
    
    try {
      // Utiliser l'API Django directement comme les autres composables
      const response = await apiDjangoAuth('/personnels/') as any
      
      const data = response.results || response
      
      if (Array.isArray(data)) {
        personnels.value = data.map(p => {
          const type = extractTypeFromGroups(p)
          return {
            id: p.id,
            matricule: p.matricule || '',
            type: type,
            personne: {
              nom: p.personne?.nom || '',
              prenom: p.personne?.prenom || '',
              email: p.personne?.email || ''
            }
          }
        })
      }
    } catch (err: any) {
      error.value = err.message || 'Erreur lors du chargement des personnels'
    } finally {
      loading.value = false
    }
  }

  function getPersonnelById(id: number): PersonnelOption | undefined {
    return personnels.value.find(p => p.id === id)
  }

  return {
    personnels,
    personnelsForSelect,
    loading,
    error,
    fetchPersonnels,
    getPersonnelById
  }
}
